import React, { useState } from 'react';
import { CaretRightOutlined } from '@ant-design/icons';

const CollapsibleSection = ({
  title,
  children,
  defaultOpen = false,
  icon,
  badge,
  variant = 'default', // 'default', 'card', 'minimal'
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const variants = {
    default: {
      wrapper: 'my-3 border border-gray-200 rounded-lg overflow-hidden',
      header: 'px-4 py-3 bg-gray-50 hover:bg-gray-100',
      content: 'px-4 py-3 bg-white',
    },
    card: {
      wrapper: 'my-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden',
      header: 'px-4 py-3 hover:bg-gray-50',
      content: 'px-4 py-3 border-t border-gray-100',
    },
    minimal: {
      wrapper: 'my-2',
      header: 'py-2 hover:bg-gray-50 rounded',
      content: 'py-2 pl-6',
    },
  };

  const style = variants[variant] || variants.default;

  return (
    <div className={style.wrapper}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between text-left transition-colors ${style.header}`}
      >
        <div className="flex items-center gap-2">
          <CaretRightOutlined
            style={{
              fontSize: 12,
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
            className="text-gray-400"
          />
          {icon && <span className="text-gray-500">{icon}</span>}
          <span className="font-medium text-gray-800">{title}</span>
          {badge && (
            <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
              {badge}
            </span>
          )}
        </div>
      </button>

      <div
        style={{
          maxHeight: isOpen ? '2000px' : '0',
          opacity: isOpen ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease, opacity 0.2s ease',
        }}
      >
        <div className={style.content}>{children}</div>
      </div>
    </div>
  );
};

// Accordion - multiple collapsible sections where only one can be open
const Accordion = ({ children, allowMultiple = false }) => {
  const [openIndexes, setOpenIndexes] = useState(new Set([0]));

  const handleToggle = (index) => {
    setOpenIndexes((prev) => {
      const next = new Set(allowMultiple ? prev : []);
      if (prev.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="my-4 border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-200">
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return null;
        return React.cloneElement(child, {
          defaultOpen: openIndexes.has(index),
          onToggle: () => handleToggle(index),
          variant: 'minimal',
        });
      })}
    </div>
  );
};

// Tab component for switching between content sections
const Tabs = ({ tabs = [], defaultTab = 0 }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  return (
    <div className="my-4">
      <div className="flex border-b border-gray-200">
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === index
                ? 'text-indigo-600 border-b-2 border-indigo-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {tab.icon && <span className="mr-2">{tab.icon}</span>}
            {tab.label}
            {tab.badge && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="py-4">{tabs[activeTab]?.content}</div>
    </div>
  );
};

export { CollapsibleSection, Accordion, Tabs };
export default CollapsibleSection;
